import React from "react";
import { useTranslation } from "react-i18next";
import ProductCard from "../common/ProductCard";
import Pagination from "./Pagination";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../../../common/components/RTKQErrorCard";
import type { FilterState } from "../../productSlice";
import type { TransformedProduct } from "../../types/productApiTypes";

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  perPage: number;
}

interface ProductsGridProps {
  products: TransformedProduct[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: any;
  pagination: PaginationData;
  filters: FilterState;
  activeView: "grid" | "list";
  onViewChange: (view: "grid" | "list") => void;
  onSortChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onFilterToggle: () => void;
}

const FilterIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"></path>
  </svg>
);

const ProductsGrid: React.FC<ProductsGridProps> = ({
  products,
  isLoading,
  isFetching,
  isError,
  error,
  pagination,
  filters,
  activeView,
  onViewChange,
  onSortChange,
  onPageChange,
  onPerPageChange,
  onFilterToggle,
}) => {
  const { t } = useTranslation("common");
  const perPageOptions = [20, 30, 40];

  function renderSkeletons() {
    const skeletonCount = pagination?.perPage || 10;
    return Array(skeletonCount)
      .fill(0)
      .map((_, index) => (
        <ProductCard.Skeleton key={`skeleton-${index}`} viewMode={activeView} />
      ));
  }

  return (
    <div className="products-grid">
      <div className="products-toolbar">
        {isLoading ? (
          <div className="products-toolbar-skeleton">
            <Skeleton height={36} width={150} />
            <Skeleton height={36} width={250} />
          </div>
        ) : (
          <>
            <button
              className="filter-toggle-button"
              onClick={onFilterToggle}
              aria-label={t("productToolbar.filterButton")}
            >
              <FilterIcon />
              <span>{t("productToolbar.filterButton")}</span>
            </button>
            <div className="sorting">
              <label htmlFor="sort-products">
                {t("productToolbar.sortLabel")}
              </label>
              <select
                id="sort-products"
                className="orderby"
                value={filters.orderby}
                onChange={onSortChange}
                disabled={isFetching}
              >
                <option value="popularity">
                  {t("productToolbar.sortOptions.popularity")}
                </option>
                <option value="date">
                  {t("productToolbar.sortOptions.date")}
                </option>
                <option value="price-asc">
                  {t("productToolbar.sortOptions.priceAsc")}
                </option>
                <option value="price-desc">
                  {t("productToolbar.sortOptions.priceDesc")}
                </option>
              </select>
            </div>
            <div className="right-toolbar">
              <div className="per-page-selector">
                <span className="per-page-label">
                  {t("productToolbar.showLabel")}
                </span>
                <div className="per-page-options">
                  {perPageOptions.map((option) => (
                    <button
                      key={option}
                      className={`per-page-button ${
                        pagination.perPage === option ? "active" : ""
                      }`}
                      onClick={() => onPerPageChange(option)}
                      disabled={isFetching || pagination.perPage === option}
                      aria-pressed={pagination.perPage === option}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div className="results-count">
                {isError
                  ? "Error"
                  : t("productToolbar.totalProducts", {
                      count: pagination.totalProducts || 0,
                    })}
              </div>
              <div className="view-toggle">
                <button
                  className={`view-button grid-view ${
                    activeView === "grid" ? "active" : ""
                  }`}
                  onClick={() => onViewChange("grid")}
                  aria-label={t("productToolbar.viewAsGrid")}
                  title={t("productToolbar.viewAsGrid")}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path d="M4 4h7v7H4V4zm9 0h7v7h-7V4zm-9 9h7v7H4v-7zm9 0h7v7h-7v-7z" />
                  </svg>
                </button>
                <button
                  className={`view-button list-view ${
                    activeView === "list" ? "active" : ""
                  }`}
                  onClick={() => onViewChange("list")}
                  aria-label={t("productToolbar.viewAsList")}
                  title={t("productToolbar.viewAsList")}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path d="M4 5h16v2H4V5zm0 6h16v2H4v-2zm0 6h16v2H4v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div
        className={`products-list ${activeView}-view ${
          isFetching ? "loading" : ""
        }`}
      >
        {isLoading ? (
          renderSkeletons()
        ) : products.length > 0 ? (
          products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              viewMode={activeView}
            />
          ))
        ) : (
          <div className="no-products-found">
            <p>{t("productArchive.noProductsFound")}</p>
          </div>
        )}
      </div>

      {isError && !isLoading && (
        <RTKQErrorCard error={error} title="Error" maxHeight="300px" />
      )}

      {!isError && products.length > 0 && (
        <Pagination
          currentPage={pagination.currentPage || 1}
          totalPages={pagination.totalPages || 1}
          onPageChange={onPageChange}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ProductsGrid;
